/**
 * Утилиты для работы с компонентом RolePlayGame
 */

/**
 * Валидирует данные ролевой игры
 * @param {Object} options - Объект настроек игры
 * @param {string} context - Контекст вызова (для логов)
 * @returns {Object} Валидированные данные
 */
export const validateRolePlayGameData = (options, context = 'RolePlayGame') => {
  try {
    // Если данные не предоставлены или не являются объектом
    if (!options || typeof options !== 'object') {
      console.warn(`${context}: Некорректные данные для игры`, options);
      return createSampleRolePlayGameData();
    }

    // Проверяем наличие обязательных полей
    if (!options.character || !options.scenario || !Array.isArray(options.scenario)) {
      console.warn(`${context}: Отсутствуют обязательные поля в данных`, options);
      return createSampleRolePlayGameData();
    }

    // Проверяем информацию о персонаже
    if (!options.character.name || !options.character.role) {
      console.warn(`${context}: Некорректные данные персонажа`, options.character);
      options.character = {
        name: options.character.name || 'Персонаж',
        avatar: options.character.avatar || 'https://via.placeholder.com/100',
        role: options.character.role || 'Инструктор',
        description: options.character.description || 'Ваш персонаж в ролевой игре'
      };
    }

    // Проверяем сценарий
    if (!options.scenario.length) {
      console.warn(`${context}: Пустой сценарий`, options.scenario);
      options.scenario = createSampleRolePlayGameData().scenario;
    }

    // Проверка на корректность сцен в сценарии
    options.scenario = options.scenario.map((scene, index) => {
      // Проверяем тип сцены
      if (!scene.type || !['dialog', 'choice', 'input', 'end'].includes(scene.type)) {
        console.warn(`${context}: Некорректный тип сцены`, scene);
        return {
          type: 'dialog',
          text: `Некорректная сцена #${index + 1}`
        };
      }

      // Проверяем наличие текста
      if (!scene.text && scene.type !== 'choice' && scene.type !== 'input') {
        console.warn(`${context}: Отсутствует текст в сцене`, scene);
        scene.text = `Текст для сцены #${index + 1}`;
      }

      // Проверяем сцену выбора
      if (scene.type === 'choice') {
        if (!scene.question || !scene.choices || !Array.isArray(scene.choices) || !scene.choices.length) {
          console.warn(`${context}: Некорректные данные для сцены выбора`, scene);
          return {
            type: 'choice',
            question: scene.question || `Вопрос для сцены #${index + 1}`,
            choices: scene.choices && Array.isArray(scene.choices) && scene.choices.length
              ? scene.choices
              : [
                  { text: 'Вариант 1', nextScene: index + 1 },
                  { text: 'Вариант 2', nextScene: index + 1 }
                ]
          };
        }
      }

      // Проверяем сцену ввода
      if (scene.type === 'input') {
        if (!scene.question || !scene.correctAnswer) {
          console.warn(`${context}: Некорректные данные для сцены ввода`, scene);
          return {
            type: 'input',
            question: scene.question || `Вопрос для сцены #${index + 1}`,
            correctAnswer: scene.correctAnswer || 'ответ',
            feedback: scene.feedback || {
              correct: 'Правильно!',
              incorrect: 'Неправильно. Попробуйте еще раз.'
            }
          };
        }
      }

      return scene;
    });

    return options;
  } catch (err) {
    console.error(`${context}: Ошибка при валидации данных`, err);
    return createSampleRolePlayGameData();
  }
};

/**
 * Проверяет, являются ли данные ролевой игры корректными
 * @param {Object} options - Объект настроек игры
 * @returns {boolean} Результат проверки
 */
export const isValidRolePlayGameData = (options) => {
  if (!options || typeof options !== 'object') return false;
  if (!options.character || !options.scenario || !Array.isArray(options.scenario)) return false;
  if (!options.character.name || !options.character.role) return false;
  if (!options.scenario.length) return false;
  
  return true;
};

/**
 * Создает тестовые данные для ролевой игры
 * @returns {Object} Тестовые данные
 */
export const createSampleRolePlayGameData = () => {
  return {
    character: {
      name: 'Профессор Кодов',
      avatar: 'https://via.placeholder.com/100',
      role: 'Ученый-программист',
      description: 'Ваш наставник в мире программирования'
    },
    scenario: [
      {
        type: 'dialog',
        text: 'Приветствую в интерактивном курсе программирования! Я профессор Кодов, и сегодня мы разберемся с основами JavaScript.'
      },
      {
        type: 'dialog',
        text: 'JavaScript - это язык программирования, который используется для создания интерактивных веб-страниц.'
      },
      {
        type: 'choice',
        question: 'Что вы хотите изучить сначала?',
        choices: [
          { text: 'Переменные и типы данных', nextScene: 3 },
          { text: 'Функции', nextScene: 6 },
          { text: 'Условные операторы', nextScene: 9 }
        ]
      },
      {
        type: 'dialog',
        text: 'Отличный выбор! Переменные используются для хранения данных. В JavaScript есть несколько типов данных: строки, числа, булевы значения и другие.'
      },
      {
        type: 'dialog',
        text: 'Например, объявить переменную можно так: let myVariable = "Hello, World!";'
      },
      {
        type: 'input',
        question: 'Попробуйте написать код для объявления числовой переменной:',
        correctAnswer: 'let',
        feedback: {
          correct: 'Отлично! Вы правильно используете ключевое слово let для объявления переменной.',
          incorrect: 'Для объявления переменной в современном JavaScript используйте ключевое слово let или const.'
        },
        nextScene: 12
      },
      {
        type: 'dialog',
        text: 'Функции - это блоки кода, которые можно вызывать многократно. Они позволяют группировать код и избегать его повторения.'
      },
      {
        type: 'dialog',
        text: 'Функция объявляется так: function myFunction() { /* код функции */ }'
      },
      {
        type: 'input',
        question: 'Попробуйте написать простую функцию, которая выводит приветствие:',
        correctAnswer: 'function',
        feedback: {
          correct: 'Прекрасно! Вы правильно используете ключевое слово function для объявления функции.',
          incorrect: 'Для объявления функции используйте ключевое слово function.'
        },
        nextScene: 12
      },
      {
        type: 'dialog',
        text: 'Условные операторы позволяют выполнять различные действия в зависимости от условий. Основной условный оператор - if-else.'
      },
      {
        type: 'dialog',
        text: 'Пример условного оператора: if (условие) { /* код, если условие истинно */ } else { /* код, если условие ложно */ }'
      },
      {
        type: 'input',
        question: 'Попробуйте написать условный оператор, который проверяет, больше ли переменная x числа 10:',
        correctAnswer: 'if',
        feedback: {
          correct: 'Верно! Вы правильно используете if для создания условного оператора.',
          incorrect: 'Для условного оператора используйте ключевое слово if.'
        },
        nextScene: 12
      },
      {
        type: 'dialog',
        text: 'Поздравляю! Вы успешно прошли базовое введение в JavaScript. Теперь вы знаете о переменных, функциях и условных операторах.'
      },
      {
        type: 'dialog',
        text: 'Продолжайте практиковаться, и вскоре вы сможете создавать свои собственные интерактивные веб-приложения!'
      },
      {
        type: 'end',
        text: 'Игра завершена! Вы успешно прошли обучение основам JavaScript.'
      }
    ]
  };
}; 